import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { TRADEXRADAR_HUB_URL } from '@/config/apiConfig';

export type ConnectionStatus = 'connecting' | 'live' | 'reconnecting' | 'offline';
export interface TradeXRadarSubscriber {
  onStatus: (status: ConnectionStatus) => void;
  onUpdate: (message: unknown) => void;
  onConnected: () => void;
}

export class TradeXRadarSignalRService {
  private readonly connection;
  private readonly subscribers = new Set<TradeXRadarSubscriber>();
  private status: ConnectionStatus = 'offline';
  private starting: Promise<void> | null = null;
  private stopping: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private stopTimer: ReturnType<typeof setTimeout> | undefined;
  private listening = false;

  constructor(url: string) {
    this.connection = new HubConnectionBuilder()
      .withUrl(url)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(import.meta.env.DEV ? LogLevel.Information : LogLevel.None)
      .build();
    this.connection.onreconnecting(() => this.setStatus('reconnecting'));
    this.connection.onreconnected(() => this.connected());
    this.connection.onclose((error) => {
      if (error && import.meta.env.DEV) console.warn('TradeXRadar connection closed', error);
      this.setStatus('offline');
      this.scheduleRetry();
    });
  }

  private setStatus(status: ConnectionStatus) {
    this.status = status;
    this.subscribers.forEach((subscriber) => subscriber.onStatus(status));
  }
  private connected() {
    this.setStatus('live');
    // Also recover after first connect: a screener run may finish during negotiation.
    this.subscribers.forEach((subscriber) => subscriber.onConnected());
  }
  private readonly handleUpdate = (message: unknown) => {
    this.subscribers.forEach((subscriber) => subscriber.onUpdate(message));
  };
  private scheduleRetry() {
    if (!this.subscribers.size || this.retryTimer !== undefined) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      void this.start();
    }, 30000);
  }

  async start(): Promise<void> {
    if (this.stopping) await this.stopping;
    if (!this.subscribers.size) return;
    if (this.starting) return this.starting;
    if (this.connection.state !== HubConnectionState.Disconnected) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    this.setStatus('connecting');
    this.starting = this.connection.start().then(() => this.connected()).catch((error: unknown) => {
      if (import.meta.env.DEV) console.warn('TradeXRadar connection unavailable', error);
      this.setStatus('offline');
      this.scheduleRetry();
    }).finally(() => { this.starting = null; });
    return this.starting;
  }

  async stop(): Promise<void> {
    if (this.subscribers.size) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    if (this.listening) {
      this.connection.off('TradeXRadarUpdated', this.handleUpdate);
      this.listening = false;
    }
    if (!this.stopping) {
      this.stopping = (async () => {
        await this.connection.stop();
        await this.starting;
      })().finally(() => { this.stopping = null; });
    }
    await this.stopping;
  }

  subscribeToTradeXRadarUpdates(subscriber: TradeXRadarSubscriber): () => void {
    clearTimeout(this.stopTimer);
    this.subscribers.add(subscriber);
    subscriber.onStatus(this.status);
    if (!this.listening) {
      this.connection.on('TradeXRadarUpdated', this.handleUpdate);
      this.listening = true;
    }
    void this.start();
    return () => this.unsubscribeFromTradeXRadarUpdates(subscriber);
  }

  unsubscribeFromTradeXRadarUpdates(subscriber: TradeXRadarSubscriber): void {
    this.subscribers.delete(subscriber);
    if (!this.subscribers.size) {
      // StrictMode immediately resubscribes; don't tear down its in-flight connection.
      this.stopTimer = setTimeout(() => { void this.stop(); }, 0);
    }
  }
}

export const tradeXRadarSignalRService = new TradeXRadarSignalRService(TRADEXRADAR_HUB_URL);
